import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Value } from 'react-phone-number-input';
import {
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from 'components/ui/form';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { useTranslation } from 'react-i18next';
import { BillingAddress } from 'features/subscription/services/subscription-service';
import { useUpdateBillingAddress } from 'features/subscription/hooks/use-subscription';
import { useQueryClient } from '@tanstack/react-query';
import { COUNTRIES } from 'constant/countries';

/**
 * `EditBillingDetails` component allows the user to edit their billing address details.
 * It provides a form with validation for billing information including name, email, phone, address, and VAT.
 * The component is designed to match the style and layout of the edit-iam-profile-details component.
 *
 * @component
 * @example
 * const billingInfo = {
 *   email: 'john.doe@example.com',
 *   name: 'John Doe',
 *   phone: '+1234567890',
 *   address: '123 Main St',
 *   city: 'Zurich',
 *   state: 'Zurich',
 *   postcode: '8001',
 *   country: 'Switzerland',
 *   vat: 'CHE-123.456.789'
 * };
 *
 * <EditBillingDetails billingInfo={billingInfo} onClose={() => {}} />
 *
 * @param {Object} props - The component's props
 * @param {BillingAddress} props.billingInfo - The billing information object containing current details to be edited
 * @param {Function} props.onClose - Callback function to close the dialog/modal
 *
 * @returns {React.Element} The rendered component
 */

type FormData = {
    email: string;
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
};

type EditBillingDetailsProps = {
    billingInfo: BillingAddress;
    onClose: () => void;
    isOpen?: boolean;
};

export const EditBillingDetails: React.FC<EditBillingDetailsProps> = ({
    billingInfo,
    onClose,
    isOpen = true,
}) => {
    const { t } = useTranslation();
    const [isFormChanged, setIsFormChanged] = useState(false);
    const queryClient = useQueryClient();

    // Update billing address mutation
    const updateBillingMutation = useUpdateBillingAddress({
        onSuccess: () => {
            // Refetch billing address data
            queryClient.invalidateQueries({ queryKey: ['billing-address'] });
            onClose();
        },
        onError: (error) => {
            console.error('Failed to update billing address:', error);
        }
    });

    const form = useForm<FormData>({
        defaultValues: {
            email: '',
            name: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            postcode: '',
            country: '',
        },
    });

    const { watch, setValue, handleSubmit, control, reset } = form;
    const watchedValues = watch();

    // Helper function to reset form to original values
    const resetFormToOriginal = useCallback(() => {
        if (billingInfo) {
            const originalData = {
                email: billingInfo.email ?? '',
                name: billingInfo.name ?? '',
                phone: billingInfo.phone ?? '',
                address: billingInfo.address ?? '',
                city: billingInfo.city ?? '',
                state: billingInfo.state ?? '',
                postcode: billingInfo.postcode ?? '',
                country: billingInfo.country ?? '',
            };
            reset(originalData);
        }
    }, [billingInfo, reset]);

    useEffect(() => {
        if (billingInfo) {
            const formData = {
                email: billingInfo.email ?? '',
                name: billingInfo.name ?? '',
                phone: billingInfo.phone ?? '',
                address: billingInfo.address ?? '',
                city: billingInfo.city ?? '',
                state: billingInfo.state ?? '',
                postcode: billingInfo.postcode ?? '',
                country: billingInfo.country ?? '',
            };
            reset(formData);
        }
    }, [billingInfo, reset]);

    // Reset form when dialog is closed (handles X button)
    useEffect(() => {
        if (!isOpen) {
            resetFormToOriginal();
        }
    }, [isOpen, resetFormToOriginal]);

    useEffect(() => {
        if (!billingInfo) return;

        const initialValues = {
            email: billingInfo.email || '',
            name: billingInfo.name || '',
            phone: billingInfo.phone || '',
            address: billingInfo.address || '',
            city: billingInfo.city || '',
            state: billingInfo.state || '',
            postcode: billingInfo.postcode || '',
            country: billingInfo.country || '',
        };

        setIsFormChanged(
            watchedValues.email !== initialValues.email ||
            watchedValues.name !== initialValues.name ||
            watchedValues.phone !== initialValues.phone ||
            watchedValues.address !== initialValues.address ||
            watchedValues.city !== initialValues.city ||
            watchedValues.state !== initialValues.state ||
            watchedValues.postcode !== initialValues.postcode ||
            watchedValues.country !== initialValues.country
        );
    }, [watchedValues, billingInfo]);

    const onSubmit = async (data: FormData) => {
        if (!billingInfo.itemId) {
            console.error('No billing address itemId found');
            return;
        }

        const updateData = {
            itemId: billingInfo.itemId,
            email: data.email,
            name: data.name,
            phone: data.phone || '',
            address: data.address,
            city: data.city || '',
            state: data.state || '',
            postcode: data.postcode || '',
            country: data.country,
            vat: '', // Send empty string as per API requirement
        };

        updateBillingMutation.mutate(updateData);
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <DialogContent
            className="rounded-md sm:max-w-[700px] overflow-y-auto max-h-screen"
            onClick={handleDialogClick}
        >
            <DialogHeader>
                <DialogTitle>{t('EDIT_BILLING_DETAILS')}</DialogTitle>
                <DialogDescription>{t('UPDATE_BILLING_INFORMATION_DESCRIPTION')}</DialogDescription>
            </DialogHeader>
            <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                            control={control}
                            name="name"
                            rules={{ required: t('NAME_REQUIRED') }}
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('NAME')}*</Label>
                                    <FormControl>
                                        <Input {...field} placeholder={t('ENTER_YOUR_NAME')} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="email"
                            rules={{
                                required: t('EMAIL_REQUIRED'),
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('INVALID_EMAIL')
                                }
                            }}
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('EMAIL')}*</Label>
                                    <FormControl>
                                        <Input {...field} type="email" placeholder={t('ENTER_YOUR_EMAIL')} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('PHONE')}</Label>
                                    <FormControl>
                                        <UIPhoneInput
                                            {...field}
                                            onChange={(value: Value) => setValue('phone', value ?? '')}
                                            placeholder={t('ENTER_YOUR_PHONE_NUMBER')}
                                            defaultCountry="CH"
                                            countryCallingCodeEditable={false}
                                            international
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="address"
                            rules={{ required: t('ADDRESS_REQUIRED') }}
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('ADDRESS')}*</Label>
                                    <FormControl>
                                        <Input {...field} placeholder={t('ENTER_YOUR_ADDRESS')} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="city"
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('CITY')}</Label>
                                    <FormControl>
                                        <Input {...field} placeholder={t('ENTER_YOUR_CITY')} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="state"
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('STATE')}</Label>
                                    <FormControl>
                                        <Input {...field} placeholder={t('ENTER_YOUR_STATE')} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="postcode"
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('POSTCODE')}</Label>
                                    <FormControl>
                                        <Input {...field} placeholder={t('ENTER_YOUR_POSTCODE')} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={control}
                            name="country"
                            rules={{ required: t('COUNTRY_REQUIRED') }}
                            render={({ field }) => (
                                <FormItem>
                                    <Label>{t('COUNTRY')}*</Label>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t('SELECT_COUNTRY')} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {COUNTRIES.map((country) => (
                                                <SelectItem key={country} value={country}>
                                                    {country}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />


                    </div>

                    <DialogFooter className="mt-5 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                resetFormToOriginal();
                                onClose();
                            }}
                        >
                            {t('CANCEL')}
                        </Button>
                        <Button
                            type="submit"
                            loading={updateBillingMutation.isPending}
                            disabled={updateBillingMutation.isPending || !isFormChanged}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            {updateBillingMutation.isPending ? t('UPDATING') : t('UPDATE')}
                        </Button>
                    </DialogFooter>
                </form>
            </Form>
        </DialogContent>
    );
};